import { useContext, useState, useCallback, useRef, useEffect } from 'react';
import { LibraryManager, ResourceStatus, ResourceType } from 'libs.library_manager';
import { useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { ApiClientService, AuthContext, useApiClient } from 'libs.react_internal';
import { deduplicateResourcesByKey } from '../../../utils/deduplicate_by_key.utils';
import { DEFAULT_CUBE_DATA_SOURCE } from '../../constants/data_view.constants';
import { mergeDimensionsFromPreview, toSnakeCase } from '../../utils';
/**
 * Library create (especially CREATE_VERSION with a shared `key`) can merge cube `data`
 * from the source and drop or inherit `data_source`. Reconcile persisted data with
 * the user's infrastructure toggle when the create response does not match.
 */
const ensureInfrastructureDataSource = async (libraryManager, resource, useNewInfrastructure) => {
    const hasNewInfrastructure = resource.data?.data_source === DEFAULT_CUBE_DATA_SOURCE;
    if (useNewInfrastructure === hasNewInfrastructure) {
        return resource;
    }
    if (useNewInfrastructure) {
        const updated = await libraryManager.updateResource(resource.id, ResourceType.CUBE_CONFIG, {
            data: {
                ...(resource.data || {}),
                data_source: DEFAULT_CUBE_DATA_SOURCE,
            },
        });
        return updated;
    }
    const { data_source: _dataSource, ...legacyData } = (resource.data || {});
    const updated = await libraryManager.updateResource(resource.id, ResourceType.CUBE_CONFIG, {
        data: legacyData,
    });
    return updated;
};
/**
 * Default Cube `format` for a dropdown-built measure. `Count` compiles to
 * `count(distinct dimension)`, which is always a whole number, so it gets an explicit
 * decimal-free format; the others (Sum/Average/Min/Max) depend on the dimension's data type,
 * so they keep the bare 'number' format, which renders without decimals only when the value
 * itself happens to be a whole number (see `formatCubeValue` in cube_format.utils.ts).
 */
const getPredefinedMeasureFormat = (measure) => measure.measureFunction === 'Count' ? 'number_0' : 'number';
// Helper to generate SQL for a measure
const generateMeasureSql = (measure) => {
    let sql = measure.sql || '';
    if (measure.calculationType === 'DROPDOWN' && measure.measureFunction && measure.dimension) {
        // Format: function(dimension), e.g., "max(response_value)"
        // Map function names to lowercase SQL function names
        const functionMap = {
            Count: 'count',
            Sum: 'sum',
            Average: 'avg',
            Min: 'min',
            Max: 'max',
        };
        const sqlFunction = functionMap[measure.measureFunction] || measure.measureFunction.toLowerCase();
        if (sqlFunction === 'count') {
            sql = `${sqlFunction}(distinct ${measure.dimension})`;
        }
        else {
            sql = `${sqlFunction}(${measure.dimension})`;
        }
    }
    return sql;
};
const normalizeTitle = (value) => value.trim().toLowerCase();
const getDuplicateMemberTitle = (dimensions, measures) => {
    const seen = new Set();
    for (const dimension of Object.values(dimensions || {})) {
        const title = dimension?.title?.trim();
        if (!title) {
            continue;
        }
        const normalized = normalizeTitle(title);
        if (seen.has(normalized)) {
            return title;
        }
        seen.add(normalized);
    }
    for (const measure of Object.values(measures || {})) {
        const title = measure?.title?.trim();
        if (!title) {
            continue;
        }
        const normalized = normalizeTitle(title);
        if (seen.has(normalized)) {
            return title;
        }
        seen.add(normalized);
    }
    return null;
};
export const useDataViews = () => {
    const { entityId } = useContext(AuthContext);
    const libraryClient = useApiClient(ApiClientService.LIBRARY);
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const [isLoading, setIsLoading] = useState(false);
    // Use refs to store latest values without causing callback recreation
    const translateRef = useRef(translate);
    const addNotificationRef = useRef(addNotification);
    const libraryClientRef = useRef(libraryClient);
    const entityIdRef = useRef(entityId);
    useEffect(() => {
        translateRef.current = translate;
        addNotificationRef.current = addNotification;
        libraryClientRef.current = libraryClient;
        entityIdRef.current = entityId;
    }, [translate, addNotification, libraryClient, entityId]);
    const fetchDataViews = useCallback(async (showArchived = false, status, search) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const response = await libraryManager.getResources(ResourceType.CUBE_CONFIG, {
                entityId: entityIdRef.current,
                active: showArchived ? 'false' : 'true',
                defaultLibrary: true,
                ...(status ? { status } : {}),
                ...(search ? { search } : {}),
            });
            const result = response.data || [];
            // SSP-4186: collapse `key` chains to a single row (highest version) so
            // that partial version-upgrade workflows (or any state that leaves
            // multiple active resources under the same `key`) do not surface as
            // duplicate list entries. Mirrors the same fix in useDashboards and
            // useCharts. The "Show archived" view still shows every version because
            // auditing across an entire chain requires the full history.
            return showArchived ? result : deduplicateResourcesByKey(result);
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error fetching datasets'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to load datasets'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const fetchDataViewById = useCallback(async (viewId) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const resource = await libraryManager.getResourceById(viewId, ResourceType.CUBE_CONFIG);
            return resource;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error fetching dataset'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to load dataset'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const createDataView = useCallback(async (formData) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const createRequest = {
                name: formData.name,
                type: ResourceType.CUBE_CONFIG,
                status: ResourceStatus.DRAFT,
                entityId: entityIdRef.current?.toString(),
                data: {
                    name: toSnakeCase(formData.name),
                    sql: '',
                    ...(formData.useNewInfrastructure ? { data_source: DEFAULT_CUBE_DATA_SOURCE } : {}),
                    dimensions: {},
                    measures: {},
                    public: true,
                },
                metadata: {
                    description: formData.description || '',
                    full_name: formData.name,
                    short_name: toSnakeCase(formData.name),
                },
            };
            const resource = await libraryManager.createResource(createRequest, ResourceType.CUBE_CONFIG);
            const dataView = await ensureInfrastructureDataSource(libraryManager, resource, !!formData.useNewInfrastructure);
            addNotificationRef.current({
                title: translateRef.current('Dataset created successfully'),
                type: 'success',
            });
            return dataView;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error creating dataset'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to create dataset'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const updateDataView = useCallback(async (viewId, updates, options) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const existing = await libraryManager.getResourceById(viewId, ResourceType.CUBE_CONFIG);
            const existingData = (existing?.data || {});
            const updatesData = (updates.data || {});
            const replaceDimensions = !!options?.replaceDimensions;
            const replaceMeasures = !!options?.replaceMeasures;
            const mergedData = updates.data
                ? {
                    ...existingData,
                    ...updatesData,
                    ...(updatesData.dimensions
                        ? {
                            dimensions: {
                                ...(replaceDimensions
                                    ? {}
                                    : existingData.dimensions || {}),
                                ...updatesData.dimensions,
                            },
                        }
                        : {}),
                    ...(updatesData.measures
                        ? {
                            measures: {
                                ...(replaceMeasures ? {} : existingData.measures || {}),
                                ...updatesData.measures,
                            },
                        }
                        : {}),
                }
                : undefined;
            const duplicateTitle = getDuplicateMemberTitle(mergedData?.dimensions, mergedData?.measures);
            if (duplicateTitle) {
                addNotificationRef.current({
                    title: translateRef.current('Duplicate title found'),
                    subtitle: translateRef.current('Dimension and measure names must be unique across the dataset'),
                    type: 'error',
                });
                throw new Error(`Duplicate dimension/measure title: ${duplicateTitle}`);
            }
            const updateRequest = {
                data: mergedData,
                metadata: updates.metadata,
            };
            if (updates.name) {
                updateRequest.name = updates.name;
                updateRequest.metadata = {
                    ...(updates.metadata || {}),
                    full_name: updates.name,
                    short_name: toSnakeCase(updates.name),
                };
                // Keep data.name (cube name) in sync with the resource name
                const dataObj = (updateRequest.data || existingData);
                updateRequest.data = { ...dataObj, name: toSnakeCase(updates.name) };
            }
            if (updates.status) {
                updateRequest.status = updates.status;
            }
            // Cast needed because UpdateResourceRequest doesn't include 'name', but the API accepts it
            const resource = await libraryManager.updateResource(viewId, ResourceType.CUBE_CONFIG, updateRequest);
            addNotificationRef.current({
                title: translateRef.current('Dataset updated successfully'),
                type: 'success',
            });
            return resource;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error updating dataset'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to update dataset'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const deleteDataView = useCallback(async (viewId) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            await libraryManager.archiveResource(viewId, ResourceType.CUBE_CONFIG);
            addNotificationRef.current({
                title: translateRef.current('Dataset deleted successfully'),
                type: 'success',
            });
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error deleting dataset'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to delete dataset'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    /**
     * Archive a dataset.
     *
     * When `options.silent` is true the hook suppresses its built-in success/error
     * toasts so compound flows (SSP-4186) can emit a single summary toast.
     */
    const archiveDataView = useCallback(async (dataView, options) => {
        setIsLoading(true);
        try {
            // Use LibraryManager.archiveResource which makes a DELETE request and sets active: false
            const archiveLibraryManager = new LibraryManager({
                axiosInstance: libraryClientRef.current,
            });
            const archivedView = await archiveLibraryManager.archiveResource(dataView.id, ResourceType.CUBE_CONFIG);
            if (!options?.silent) {
                addNotificationRef.current({
                    title: translateRef.current('Dataset archived successfully'),
                    type: 'success',
                });
            }
            return archivedView;
        }
        catch (error) {
            const axiosError = error;
            if (!options?.silent) {
                addNotificationRef.current({
                    title: translateRef.current('Error archiving dataset'),
                    subtitle: axiosError.response?.data?.message || translateRef.current('Failed to archive dataset'),
                    type: 'error',
                });
            }
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    /**
     * Clone an existing dataset (or create a new version of it).
     *
     * When `options.silent` is true the hook suppresses its built-in success/error
     * toasts so compound flows (SSP-4186) can emit a single outcome toast covering
     * both the clone and a follow-up archive.
     */
    const cloneDataView = useCallback(async (sourceView, option = 'CLONE', overrides, options) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            // Create a new view with cloned data
            const clonedName = overrides?.name || sourceView.name;
            const clonedDescription = overrides?.description ?? sourceView.metadata?.description ?? '';
            const sourceData = (sourceView.data || {});
            // Remove studyId and environmentId as they are not part of the dataset configuration
            // (they were only used for preview purposes and should not be persisted).
            // Strip data_source so the new infrastructure choice is set explicitly below
            // rather than inherited from the source.
            const { studyId: _studyId, environmentId: _envId, data_source: sourceDataSource, ...cleanedSourceData } = sourceData;
            // When the caller passes `useNewInfrastructure` (toggle in Create / Change status / clone
            // flows) it overrides the source; otherwise we mirror the source's data_source for
            // programmatic clones without a UI toggle.
            const useNewInfrastructure = overrides?.useNewInfrastructure !== undefined
                ? overrides.useNewInfrastructure
                : sourceDataSource === DEFAULT_CUBE_DATA_SOURCE;
            const createRequest = {
                name: clonedName,
                type: ResourceType.CUBE_CONFIG,
                status: ResourceStatus.DRAFT,
                entityId: entityIdRef.current?.toString(),
                data: {
                    ...cleanedSourceData,
                    name: toSnakeCase(clonedName), // data.name should be snake_cased
                    public: true,
                    ...(useNewInfrastructure ? { data_source: DEFAULT_CUBE_DATA_SOURCE } : {}),
                },
                metadata: {
                    description: clonedDescription,
                },
            };
            // If creating a new version, include the key (source view's key or ID) in the payload
            // The key is used to group versions of the same dataset together
            if (option === 'CREATE_VERSION') {
                // Use the source view's key if it exists, otherwise use its ID
                createRequest.key = sourceView.key || sourceView.id;
            }
            const resource = await libraryManager.createResource(createRequest, ResourceType.CUBE_CONFIG);
            const dataView = await ensureInfrastructureDataSource(libraryManager, resource, useNewInfrastructure);
            if (!options?.silent) {
                addNotificationRef.current({
                    title: option === 'CREATE_VERSION'
                        ? translateRef.current('Dataset version created successfully')
                        : translateRef.current('Dataset cloned successfully'),
                    type: 'success',
                });
            }
            return dataView;
        }
        catch (error) {
            const axiosError = error;
            if (!options?.silent) {
                addNotificationRef.current({
                    title: option === 'CREATE_VERSION'
                        ? translateRef.current('Error creating dataset version')
                        : translateRef.current('Error cloning dataset'),
                    subtitle: axiosError.response?.data?.message || translateRef.current('Failed to clone dataset'),
                    type: 'error',
                });
            }
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    // Returns null when the name is unique, or a translated error message
    // describing the conflict otherwise. Centralising the message lets us
    // explain the snake_case collision case (SSP-3728), where the new name
    // looks unique to the user but maps to the same internal identifier as
    // an existing dataset (e.g. "rj_dataset (clone 1)" vs "rj_dataset_clone_1").
    const checkViewNameUnique = useCallback(async (name, excludeId) => {
        try {
            const views = await fetchDataViews();
            const candidateName = name.toLowerCase();
            const candidateDataName = toSnakeCase(name);
            const displayMatch = views.find((view) => view.id !== excludeId && view.name.toLowerCase() === candidateName);
            if (displayMatch) {
                return translateRef.current('A dataset with this name already exists');
            }
            if (candidateDataName) {
                const internalMatch = views.find((view) => {
                    if (view.id === excludeId) {
                        return false;
                    }
                    const existingDataName = view.data?.name || toSnakeCase(view.name);
                    return existingDataName === candidateDataName;
                });
                if (internalMatch) {
                    return translateRef.current('This name resolves to the same internal identifier ("{shortName}") as the existing dataset "{conflictName}". Please choose a more distinct name.', { shortName: candidateDataName, conflictName: internalMatch.name });
                }
            }
            return null;
        }
        catch (error) {
            // If fetch fails, treat the name as unique so the user can proceed;
            // the server will reject any genuine duplicate at submit time.
            return null;
        }
    }, [fetchDataViews]);
    const addDimension = useCallback(async (viewId, dimension) => {
        setIsLoading(true);
        try {
            // First fetch the current view to get existing dimensions
            const currentView = await fetchDataViewById(viewId);
            const updatedDimensions = {
                ...(currentView.data?.dimensions || {}),
                [dimension.name]: {
                    sql: dimension.sql,
                    type: dimension.type,
                    title: dimension.name,
                    description: dimension.description,
                },
            };
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const resource = await libraryManager.updateResource(viewId, ResourceType.CUBE_CONFIG, {
                data: {
                    ...currentView.data,
                    dimensions: updatedDimensions,
                },
            });
            addNotificationRef.current({
                title: translateRef.current('Dimension added successfully'),
                type: 'success',
            });
            return resource;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error adding dimension'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to add dimension'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, [fetchDataViewById]);
    const addDimensions = useCallback(async (viewId, dimensions, sqlQuery, currentView) => {
        setIsLoading(true);
        try {
            const updatedDimensions = {
                ...(currentView.data?.dimensions || {}),
            };
            // Add all new dimensions at once
            for (const dimension of dimensions) {
                const dimensionKey = toSnakeCase(dimension.name);
                updatedDimensions[dimensionKey] = {
                    sql: dimension.sql,
                    type: dimension.type,
                    title: dimension.title || dimension.name, // Use provided title or fallback to name
                    description: dimension.description,
                };
            }
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const resource = await libraryManager.updateResource(viewId, ResourceType.CUBE_CONFIG, {
                data: {
                    ...currentView.data,
                    dimensions: updatedDimensions,
                    ...(sqlQuery ? { sql: sqlQuery } : {}),
                },
            });
            addNotificationRef.current({
                title: translateRef.current('Dimensions added successfully'),
                type: 'success',
            });
            return resource;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error adding dimensions'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to add dimensions'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const updateDimension = useCallback(async (viewId, dimensionName, dimension) => {
        setIsLoading(true);
        try {
            const currentView = await fetchDataViewById(viewId);
            const updatedDimensions = {
                ...(currentView.data?.dimensions || {}),
            };
            // If name changed, remove old and add new
            if (dimensionName !== dimension.name) {
                delete updatedDimensions[dimensionName];
            }
            updatedDimensions[dimension.name] = {
                sql: dimension.sql,
                type: dimension.type,
                title: dimension.name,
                description: dimension.description,
            };
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const resource = await libraryManager.updateResource(viewId, ResourceType.CUBE_CONFIG, {
                data: {
                    ...currentView.data,
                    dimensions: updatedDimensions,
                },
            });
            addNotificationRef.current({
                title: translateRef.current('Dimension updated successfully'),
                type: 'success',
            });
            return resource;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error updating dimension'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to update dimension'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, [fetchDataViewById]);
    const updateDimensions = useCallback(async (viewId, dimensions, sqlQuery, currentView) => {
        setIsLoading(true);
        try {
            // Merge preview columns with existing Data settings (SSP-4237).
            // Matching keys keep title/description/format/public; removed columns are dropped.
            const updatedDimensions = mergeDimensionsFromPreview(currentView.data?.dimensions, dimensions);
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const resource = await libraryManager.updateResource(viewId, ResourceType.CUBE_CONFIG, {
                data: {
                    ...currentView.data,
                    dimensions: updatedDimensions,
                    ...(sqlQuery ? { sql: sqlQuery } : {}),
                },
            });
            addNotificationRef.current({
                title: translateRef.current('Dimensions updated successfully'),
                type: 'success',
            });
            return resource;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error updating dimensions'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to update dimensions'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const deleteDimension = useCallback(async (viewId, dimensionName) => {
        setIsLoading(true);
        try {
            const currentView = await fetchDataViewById(viewId);
            const updatedDimensions = {
                ...(currentView.data?.dimensions || {}),
            };
            delete updatedDimensions[dimensionName];
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const resource = await libraryManager.updateResource(viewId, ResourceType.CUBE_CONFIG, {
                data: {
                    ...currentView.data,
                    dimensions: updatedDimensions,
                },
            });
            addNotificationRef.current({
                title: translateRef.current('Dimension deleted successfully'),
                type: 'success',
            });
            return resource;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error deleting dimension'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to delete dimension'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, [fetchDataViewById]);
    const addMeasure = useCallback(async (viewId, measure, currentView) => {
        setIsLoading(true);
        try {
            // Generate SQL based on calculation type
            const sql = generateMeasureSql(measure);
            const measureKey = toSnakeCase(measure.name);
            const isPredefined = measure.calculationType === 'DROPDOWN';
            const updatedMeasures = {
                ...(currentView.data?.measures || {}),
                [measureKey]: {
                    sql,
                    type: measure.type || 'number',
                    title: measure.name,
                    description: measure.description,
                    format: isPredefined ? getPredefinedMeasureFormat(measure) : undefined,
                    meta: {
                        isPredefined,
                    },
                },
            };
            const duplicateTitle = getDuplicateMemberTitle(currentView.data?.dimensions, updatedMeasures);
            if (duplicateTitle) {
                addNotificationRef.current({
                    title: translateRef.current('Duplicate title found'),
                    subtitle: translateRef.current('Dimension and measure names must be unique across the dataset'),
                    type: 'error',
                });
                throw new Error(`Duplicate dimension/measure title: ${duplicateTitle}`);
            }
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const resource = await libraryManager.updateResource(viewId, ResourceType.CUBE_CONFIG, {
                data: {
                    ...currentView.data,
                    measures: updatedMeasures,
                },
            });
            addNotificationRef.current({
                title: translateRef.current('Measure added successfully'),
                type: 'success',
            });
            return resource;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error adding measure'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to add measure'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    /**
     * Adds a batch of measures in a single resource update (one PUT request).
     * Callers are expected to validate the batch first (see validateMeasureDrafts);
     * the duplicate-title guard here is the same final integrity check the
     * single-measure flow performs.
     */
    const addMeasures = useCallback(async (viewId, measures, currentView) => {
        setIsLoading(true);
        try {
            const updatedMeasures = {
                ...(currentView.data?.measures || {}),
            };
            for (const measure of measures) {
                const sql = generateMeasureSql(measure);
                const isPredefined = measure.calculationType === 'DROPDOWN';
                updatedMeasures[toSnakeCase(measure.name)] = {
                    sql,
                    type: measure.type || 'number',
                    title: measure.name,
                    description: measure.description,
                    format: isPredefined ? getPredefinedMeasureFormat(measure) : undefined,
                    meta: {
                        isPredefined,
                    },
                };
            }
            const duplicateTitle = getDuplicateMemberTitle(currentView.data?.dimensions, updatedMeasures);
            if (duplicateTitle) {
                addNotificationRef.current({
                    title: translateRef.current('Duplicate title found'),
                    subtitle: translateRef.current('Dimension and measure names must be unique across the dataset'),
                    type: 'error',
                });
                throw new Error(`Duplicate dimension/measure title: ${duplicateTitle}`);
            }
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const resource = await libraryManager.updateResource(viewId, ResourceType.CUBE_CONFIG, {
                data: {
                    ...currentView.data,
                    measures: updatedMeasures,
                },
            });
            addNotificationRef.current({
                title: translateRef.current('{count, plural, one {# measure} other {# measures}} added successfully', {
                    count: measures.length,
                }),
                type: 'success',
            });
            return resource;
        }
        catch (error) {
            console.error('Error adding measures in bulk:', error);
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error adding measures'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to add measures'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const updateMeasure = useCallback(async (viewId, measureName, measure, currentView) => {
        setIsLoading(true);
        try {
            // Generate SQL based on calculation type
            const sql = generateMeasureSql(measure);
            // Preserve the existing measure's title (display name), not the snake_cased key
            const existingMeasure = currentView.data?.measures?.[measureName];
            const isPredefined = measure.calculationType === 'DROPDOWN';
            const updatedMeasures = {
                ...(currentView.data?.measures || {}),
                [measureName]: {
                    sql,
                    type: measure.type || 'number',
                    title: existingMeasure?.title || measure.name,
                    description: measure.description,
                    format: isPredefined ? getPredefinedMeasureFormat(measure) : undefined,
                    meta: {
                        isPredefined,
                    },
                },
            };
            const duplicateTitle = getDuplicateMemberTitle(currentView.data?.dimensions, updatedMeasures);
            if (duplicateTitle) {
                addNotificationRef.current({
                    title: translateRef.current('Duplicate title found'),
                    subtitle: translateRef.current('Dimension and measure names must be unique across the dataset'),
                    type: 'error',
                });
                throw new Error(`Duplicate dimension/measure title: ${duplicateTitle}`);
            }
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const resource = await libraryManager.updateResource(viewId, ResourceType.CUBE_CONFIG, {
                data: {
                    ...currentView.data,
                    measures: updatedMeasures,
                },
            });
            addNotificationRef.current({
                title: translateRef.current('Measure updated successfully'),
                type: 'success',
            });
            return resource;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error updating measure'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to update measure'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const cloneMeasure = useCallback(async (viewId, sourceMeasureId, newName, newDescription) => {
        setIsLoading(true);
        try {
            const currentView = await fetchDataViewById(viewId);
            const sourceMeasure = currentView.data?.measures?.[sourceMeasureId];
            if (!sourceMeasure) {
                throw new Error(`Source measure "${sourceMeasureId}" not found`);
            }
            const measureKey = toSnakeCase(newName);
            const updatedMeasures = {
                ...(currentView.data?.measures || {}),
                [measureKey]: {
                    ...sourceMeasure,
                    title: newName,
                    description: newDescription,
                },
            };
            const duplicateTitle = getDuplicateMemberTitle(currentView.data?.dimensions, updatedMeasures);
            if (duplicateTitle) {
                addNotificationRef.current({
                    title: translateRef.current('Duplicate title found'),
                    subtitle: translateRef.current('Dimension and measure names must be unique across the dataset'),
                    type: 'error',
                });
                throw new Error(`Duplicate dimension/measure title: ${duplicateTitle}`);
            }
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const resource = await libraryManager.updateResource(viewId, ResourceType.CUBE_CONFIG, {
                data: {
                    ...currentView.data,
                    measures: updatedMeasures,
                },
            });
            addNotificationRef.current({
                title: translateRef.current('Measure cloned successfully'),
                type: 'success',
            });
            return resource;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error cloning measure'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to clone measure'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, [fetchDataViewById]);
    const deleteMeasure = useCallback(async (viewId, measureName) => {
        setIsLoading(true);
        try {
            // First fetch the current view to get existing measures
            const currentView = await fetchDataViewById(viewId);
            const updatedMeasures = { ...(currentView.data?.measures || {}) };
            delete updatedMeasures[measureName];
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const resource = await libraryManager.updateResource(viewId, ResourceType.CUBE_CONFIG, {
                data: {
                    ...currentView.data,
                    measures: updatedMeasures,
                },
            });
            addNotificationRef.current({
                title: translateRef.current('Measure deleted successfully'),
                type: 'success',
            });
            return resource;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error deleting measure'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to delete measure'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, [fetchDataViewById]);
    return {
        fetchDataViews,
        fetchDataViewById,
        createDataView,
        updateDataView,
        deleteDataView,
        archiveDataView,
        cloneDataView,
        checkViewNameUnique,
        addDimension,
        addDimensions,
        updateDimension,
        updateDimensions,
        deleteDimension,
        addMeasure,
        addMeasures,
        updateMeasure,
        cloneMeasure,
        deleteMeasure,
        isLoading,
    };
};
